import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef, type Ref } from 'vue'

import { OlympusClient } from '@/OlympusClient'

import { MEDIA_QUERY_KEYS } from './constants'
import type { GalleryItem, MediaWithPurpose } from './types'

interface UseImageGalleryParams {
  menuItemId: Ref<string>
  enabled: Ref<boolean>
}

interface UseImageGalleryReturn {
  galleryItems: ComputedRef<GalleryItem[]>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  refetch: () => Promise<void>
}

/**
 * Composable for fetching and managing gallery items for a menu item
 * @param params - Parameters containing menuItemId and enabled flag
 */
export function useImageGallery(
  params: UseImageGalleryParams
): UseImageGalleryReturn {
  const {
    data: mediaData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    enabled: computed(() => params.enabled.value),
    queryFn: async () => {
      const response = await OlympusClient.media.v1.menuItem.menuItem.listMedia(
        {
          params: { itemId: params.menuItemId.value },
          query: { purpose: 'suggested_image' },
        }
      )
      return response
    },
    queryKey: computed(() =>
      MEDIA_QUERY_KEYS.menuItemMedia(params.menuItemId.value, 'suggested_image')
    ),
    staleTime: 30_000,
  })

  const galleryItems = computed<GalleryItem[]>(() => {
    if (!mediaData.value) {
      return []
    }

    const items: GalleryItem[] = mediaData.value.results
      .filter((item: MediaWithPurpose) => item.media !== null)
      .map((item: MediaWithPurpose) => ({
        id: item.media.id,
        media: item.media,
        state: 'ready' as const,
      }))
      .sort((a, b) => {
        const dateA = new Date(a.media.createdAt).getTime()
        const dateB = new Date(b.media.createdAt).getTime()
        return dateB - dateA
      })

    items.push({
      id: 'placeholder',
      media: null,
      state: 'placeholder',
    })

    return items
  })

  return {
    error: error as Ref<Error | null>,
    galleryItems,
    isLoading,
    refetch: async () => {
      await refetch()
    },
  }
}
